import { App, Checkbox, Form } from "antd"
import { orderBy } from "lodash"
import { motion } from "motion/react"
import { useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import AddStudentTaskForm from "#components/views/AdminView/StudentTaskList/AddStudentTaskForm"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { AddStudentFormValues } from "#customTypes/composedTypes/studentTypes"
import { invariant } from "#customTypes/types"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useErrorModal } from "#hooks/useErrorModal"
import { ErrorType } from "#utils/textFormatters"

const StudentTasksList = () => {
  const form = Form.useFormInstance<AddStudentFormValues>()
  const studentId = form.getFieldValue("id")
  const student = useGetOneStudent(studentId)
  const { currentUser } = useUserContext()
  const [showCompletedTasks, setShowCompletedTasks] = useState(false)

  const openErrorModal = useErrorModal("viewing StudentTasksList")
  const { message } = App.useApp()
  const theme = useThemeContext()

  const tasks = useMemo(() => {
    if (!student?.tasks) return []
    return orderBy(
      student.tasks,
      ["completedAt", "createdAt"],
      ["desc", "asc"]
    ).filter((task) => (showCompletedTasks ? true : !task.isCompleted))
  }, [student?.tasks, showCompletedTasks])

  const handleCheckTaskBox = async (taskId: string) => {
    invariant(!!student, "Student doesn't exist")
    try {
      const taskToUpdate = student.tasks?.find((task) => task.id === taskId)
      invariant(
        !!taskToUpdate,
        `Task ${taskId} not found for _student_id ${student.id}!`
      )
      const isCompleted = !taskToUpdate.isCompleted

      await Firebase.getInstance().toggleStudentTask({
        studentId: student.id,
        taskId,
        isCompleted,
        completedBy: currentUser?.id ?? "",
      })

      message.success(
        `Task ${isCompleted ? "completed" : "reset"} for ${student.name}`
      )
    } catch (error) {
      const context = `
StudentTasksList
`
      openErrorModal(error as ErrorType, context)
    }
  }

  return (
    <Flex col gap={20}>
      <Flex style={{ alignItems: "center" }} gap={10}>
        <h2>Tasks</h2>
        <AddStudentTaskForm studentId={studentId} showLabel={false} />
        <Checkbox
          onChange={(e) => setShowCompletedTasks(e.target.checked)}
          style={{ marginLeft: 20 }}
        >
          Show completed tasks
        </Checkbox>
      </Flex>

      <Flex col gap={8}>
        {tasks.length ? (
          tasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              whileHover={{ textDecoration: "underline" }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              onClick={() => handleCheckTaskBox(task.id)}
              style={{
                cursor: "pointer",
                width: "fit-content",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: task.isCompleted ? theme.disabled : theme.black,
                fontSize: "12px",
              }}
            >
              <Checkbox
                checked={task.isCompleted}
                disabled={task.isCompleted}
              />
              {task.label}
            </motion.div>
          ))
        ) : (
          <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm}>
            No tasks found
          </AnimateInFlex>
        )}
      </Flex>
    </Flex>
  )
}

export default StudentTasksList
